
import React, { useState, useEffect } from 'react';
import { Trophy, Medal, Compass, Award, Star, Flame, Target, Calendar, Salad, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface AchievementBadge {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  bgColor: string;
  textColor: string;
  dateEarned?: string;
}

interface UserAchievementsProps {
  userId: string;
  compact?: boolean;
}

const UserAchievements: React.FC<UserAchievementsProps> = ({ userId, compact = false }) => {
  const { user } = useAuth();
  const [achievements, setAchievements] = useState<AchievementBadge[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAchievements = async () => {
      if (!userId) return;
      
      setIsLoading(true);
      const userAchievements: AchievementBadge[] = [];
      
      try {
        // Check for completed challenges directly with a debug log
        console.log(`Fetching achievements for user: ${userId}`);
        
        // Check for completed challenges from database with more detailed error handling
        try {
          const { data: challengeData, error: challengeError } = await supabase
            .from('user_challenges')
            .select('*')
            .eq('user_id', userId)
            .not('completed_at', 'is', null);
            
          if (challengeError) {
            console.error("Error fetching challenge data:", challengeError);
          } else if (challengeData && challengeData.length > 0) {
            console.log("Found completed challenges:", challengeData.length);
            console.log("Completed challenges data:", challengeData);
            
            // Add challenge completion badges based on challenge_id
            challengeData.forEach(challenge => {
              if (challenge.challenge_id === 1) {
                userAchievements.push({
                  id: 'challenge_1',
                  name: 'Reto Inicial',
                  description: 'Completó el primer reto de la plataforma',
                  icon: <Star className="h-4 w-4" />,
                  bgColor: 'bg-amber-100',
                  textColor: 'text-amber-800',
                  dateEarned: challenge.completed_at
                });
              } else if (challenge.challenge_id === 2) {
                userAchievements.push({
                  id: 'challenge_2',
                  name: 'Reto Avanzado',
                  description: 'Completó el segundo reto de la plataforma',
                  icon: <Flame className="h-4 w-4" />,
                  bgColor: 'bg-red-100',
                  textColor: 'text-red-800',
                  dateEarned: challenge.completed_at
                });
              } else if (challenge.challenge_id === 3) {
                userAchievements.push({
                  id: 'challenge_3',
                  name: 'Reto Maestro',
                  description: 'Completó el tercer reto de la plataforma',
                  icon: <Award className="h-4 w-4" />,
                  bgColor: 'bg-indigo-100',
                  textColor: 'text-indigo-800',
                  dateEarned: challenge.completed_at
                });
              } else if (challenge.challenge_id === 4) {
                userAchievements.push({
                  id: 'challenge_4',
                  name: 'Crítico Gourmet',
                  description: 'Ha valorado 3 o más recetas en la plataforma',
                  icon: <Target className="h-4 w-4" />,
                  bgColor: 'bg-green-100',
                  textColor: 'text-green-800',
                  dateEarned: challenge.completed_at
                });
              }
              // Add more challenge badges as needed
            });
          } else {
            console.log("No completed challenges found in database");
          }
        } catch (dbError) {
          console.error("Database error fetching challenges:", dbError);
          toast.error("Error al cargar tus logros", { id: "achievements-error" });
        }
        
        // Check for nutrition journal achievements
        try {
          const { data: journalData, error: journalError } = await supabase
            .from('nutrition_journals')
            .select('*')
            .eq('user_id', userId);
            
          if (journalError) {
            console.error("Error fetching journal data:", journalError);
          } else if (journalData && journalData.length > 0) {
            console.log("Journal entries found:", journalData.length);
            
            // Journal consistency badge for 7+ entries
            if (journalData.length >= 7) {
              userAchievements.push({
                id: 'consistency',
                name: 'Constancia Vegetal',
                description: 'Ha mantenido un seguimiento de su diario nutricional durante 7 días o más',
                icon: <Calendar className="h-4 w-4" />,
                bgColor: 'bg-blue-100',
                textColor: 'text-blue-800',
                dateEarned: journalData[journalData.length - 1].created_at
              });
            }
          } else {
            console.log("No journal entries found");
          }
        } catch (dbError) {
          console.error("Database error fetching journal entries:", dbError);
        }
        
        // Check local storage for other achievements
        
        // Check for social 5 badge
        const hasSocialBadge = localStorage.getItem(`badge_social5_${userId}`) === 'true' || 
                              sessionStorage.getItem(`badge_social5_${userId}`) === 'true';
        
        if (hasSocialBadge) {
          userAchievements.push({
            id: 'social5',
            name: '5 Social',
            description: 'Ha completado el reto social interactuando con 5 usuarios',
            icon: <Users className="h-4 w-4" />,
            bgColor: 'bg-pink-100',
            textColor: 'text-pink-800',
            dateEarned: localStorage.getItem(`badge_social5_date_${userId}`) || undefined
          });
        }
        
        // Check for explorer badge - also check shorter version of key without the "5" suffix
        const hasExplorerBadge = localStorage.getItem(`badge_explorer_${userId}`) === 'true' || 
                               sessionStorage.getItem(`badge_explorer_${userId}`) === 'true' ||
                               localStorage.getItem(`badge_social_${userId}`) === 'true' || 
                               sessionStorage.getItem(`badge_social_${userId}`) === 'true';
        
        if (hasExplorerBadge) {
          userAchievements.push({
            id: 'explorer',
            name: 'Explorador Vegano',
            description: 'Ha completado el reto Explorador Vegano explorando diferentes recetas',
            icon: <Compass className="h-4 w-4" />,
            bgColor: 'bg-purple-100',
            textColor: 'text-purple-800',
            dateEarned: localStorage.getItem(`badge_explorer_date_${userId}`) || 
                        localStorage.getItem(`badge_social_date_${userId}`) || undefined
          });
        }
        
        // Check for consistency badge (from localStorage)
        const hasConsistencyBadge = localStorage.getItem(`badge_consistency_${userId}`) === 'true';
        
        if (hasConsistencyBadge) {
          userAchievements.push({
            id: 'consistency_local',
            name: 'Constancia Vegetal',
            description: 'Ha mantenido un seguimiento de su diario nutricional durante 7 días consecutivos',
            icon: <Calendar className="h-4 w-4" />,
            bgColor: 'bg-blue-100',
            textColor: 'text-blue-800',
            dateEarned: localStorage.getItem(`badge_consistency_date_${userId}`) || undefined
          });
        }
        
        // Check for recipe master badge
        const hasRecipeMasterBadge = localStorage.getItem(`badge_recipe_master_${userId}`) === 'true';
        
        if (hasRecipeMasterBadge) {
          userAchievements.push({
            id: 'recipe_master',
            name: 'Maestro de Recetas',
            description: 'Ha guardado más de 10 recetas en sus favoritos',
            icon: <Salad className="h-4 w-4" />,
            bgColor: 'bg-green-100',
            textColor: 'text-green-800',
            dateEarned: localStorage.getItem(`badge_recipe_master_date_${userId}`) || undefined
          });
        }
        
        // Check profile favorites count for recipe master badge
        if (user?.favorites && user.favorites.length >= 10 && !hasRecipeMasterBadge) {
          // Save badge to localStorage for future reference
          localStorage.setItem(`badge_recipe_master_${userId}`, 'true');
          localStorage.setItem(`badge_recipe_master_date_${userId}`, new Date().toISOString());
          
          userAchievements.push({
            id: 'recipe_master',
            name: 'Maestro de Recetas',
            description: 'Ha guardado más de 10 recetas en sus favoritos',
            icon: <Salad className="h-4 w-4" />,
            bgColor: 'bg-green-100',
            textColor: 'text-green-800',
            dateEarned: new Date().toISOString()
          });
        }
        
        // Check the user_achievements table directly (for legacy or manual achievements)
        try {
          const { data, error } = await supabase
            .from('user_achievements')
            .select('*')
            .eq('user_id', userId);
              
          if (error) {
            console.error("Error fetching achievements data:", error);
          } else if (data && data.length > 0) {
            console.log("Achievement data from database:", data);
            data.forEach(achievement => {
              userAchievements.push({
                id: achievement.achievement_id,
                name: achievement.name || 'Logro',
                description: achievement.description || 'Has ganado un logro',
                icon: <Trophy className="h-4 w-4" />,
                bgColor: 'bg-amber-100',
                textColor: 'text-amber-800',
                dateEarned: achievement.earned_at
              });
            });
          } else {
            console.log("No achievements found in user_achievements table");
          }
        } catch (dbError) {
          console.error("Database error fetching achievements:", dbError);
        }
      } catch (error) {
        console.error("Error fetching achievements:", error);
        toast.error("Error al cargar tus logros", { id: "achievements-error" });
      } finally {
        setIsLoading(false);
        
        // Remove duplicate achievements (same id)
        const uniqueAchievements = userAchievements.filter(
          (achievement, index, self) => 
            index === self.findIndex(a => a.id === achievement.id)
        );
        
        setAchievements(uniqueAchievements);
        
        // Log achievements for debugging
        console.log("Final achievements list:", uniqueAchievements);
      }
    };
    
    fetchAchievements();
  }, [userId, user]);

  if (isLoading) {
    return (
      <div className="flex justify-center my-4">
        <div className="h-6 w-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (achievements.length === 0) {
    return (
      <div className="text-center text-gray-500 py-4">
        <Trophy className="h-10 w-10 text-gray-300 mx-auto mb-2" />
        <p>Todavía no hay logros disponibles</p>
        <p className="text-xs mt-1">Completa retos para desbloquear logros</p>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex flex-wrap gap-2">
        {achievements.map(badge => (
          <Badge 
            key={badge.id}
            variant="outline" 
            className={`${badge.bgColor} ${badge.textColor} border-none px-2 py-1 flex items-center gap-1`}
            title={badge.description}
          >
            {badge.icon}
            <span className="text-xs">{badge.name}</span>
          </Badge>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {achievements.map(badge => (
        <div key={badge.id} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${badge.bgColor}`}>
            {badge.icon}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">{badge.name}</h3>
              <Medal className="h-3 w-3 text-yellow-500" />
            </div>
            <p className="text-xs text-gray-600 mt-1">{badge.description}</p>
            {badge.dateEarned && (
              <p className="text-xs text-gray-500 mt-1">Obtenido el: {new Date(badge.dateEarned).toLocaleDateString()}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default UserAchievements;
